<script setup>
import axios from 'axios';
import { computed, ref, watch } from 'vue';
import { extractApiError } from '../apiError';
import { t } from '../i18n';
import { chipClass } from '../ui';
import { formatDay, formatMonth } from '../achievements';
import AchievementHabits from './AchievementHabits.vue';
import AchievementLadder from './AchievementLadder.vue';
import AchievementPath from './AchievementPath.vue';
import AchievementPathList from './AchievementPathList.vue';
import AchievementSteps from './AchievementSteps.vue';
import BottomSheet from './BottomSheet.vue';
import ComebackOffer from './ComebackOffer.vue';
import HabitDetail from './HabitDetail.vue';
import NextMilestone from './NextMilestone.vue';

// «Достижения» of the selected view (docs/achievements/SPEC.md, «Задача v2», «Проект v2»): where I am and when the
// next level comes; whether I speed up and thanks to what; the path of bright moments; my habits. The comeback
// offer while unanswered; the start point as a quiet row at the bottom. Details in sheets.
const props = defineProps({
  // currentView of the home screen
  portfolio: {
    type: Object,
    required: true,
  },
  // Bumped after accounts are imported again: read anew.
  dataVersion: {
    type: Number,
    default: 0,
  },
});

const data = ref(null);
const loading = ref(true);
const error = ref('');
let request = 0;

// Whole accounts, as on the growth chart (docs/performance/SPEC.md, decision 5).
const portfolioIds = computed(() => props.portfolio.members.map((m) => m.portfolio_id));

async function load() {
  const id = ++request;
  if (portfolioIds.value.length === 0) {
    data.value = null;
    loading.value = false;
    return;
  }
  loading.value = data.value === null;
  error.value = '';
  try {
    const { data: body } = await axios.get('/achievements/list', { params: { portfolio_ids: portfolioIds.value } });
    if (id === request) {
      data.value = body;
    }
  } catch (e) {
    if (id === request && e?.response?.status !== 401) {
      error.value = extractApiError(e, t('achievements.loadFailed'));
    }
  } finally {
    if (id === request) {
      loading.value = false;
    }
  }
}

watch(() => portfolioIds.value.join(','), load, { immediate: true });
watch(() => props.dataVersion, load);

const comeback = computed(() => data.value?.start?.comeback ?? null);

// One sheet: 'levels' (capital and shares), 'path', or a habit.
const sheet = ref(null);
const ladder = ref('capital');

function open(kind) {
  ladder.value = 'capital';
  sheet.value = kind;
}

const sheetTitle = computed(() => {
  switch (sheet.value) {
    case 'levels':
      return t('achievements.levelsTitle');
    case 'path':
      return t('achievements.pathTitle');
    case null:
      return '';
    default:
      return t(`achievements.habit.${sheet.value}`);
  }
});

// Start point (decision 10 and «Проект v2»): from the first operation, from the comeback, or a date.
const startSheetOpen = ref(false);
const startMode = ref('first');
const startInput = ref('');
const saving = ref(false);
const saveError = ref('');

function openStartSheet() {
  const start = data.value?.start;
  startMode.value = start?.mode ?? 'first';
  startInput.value = start?.mode === 'custom' ? start.date : '';
  saveError.value = '';
  startSheetOpen.value = true;
}

async function saveStart(date) {
  saving.value = true;
  saveError.value = '';
  try {
    await axios.put('/achievements/start', { date });
    startSheetOpen.value = false;
    await load();
  } catch (e) {
    saveError.value = extractApiError(e, t('achievements.saveFailed'));
  } finally {
    saving.value = false;
  }
}

const startChoice = computed(() => {
  if (startMode.value === 'first') {
    return null;
  }
  return startMode.value === 'return' ? comeback.value?.date ?? null : startInput.value || null;
});

function applyStart() {
  if (startMode.value !== 'custom' || startInput.value) {
    saveStart(startChoice.value);
  }
}

// The comeback card answers in place: an error stays on the card, not over the whole screen.
const comebackError = ref('');

async function answerComeback(accept) {
  saving.value = true;
  comebackError.value = '';
  try {
    if (accept) {
      await axios.put('/achievements/start', { date: comeback.value.date });
    } else {
      await axios.put('/achievements/comeback', { date: comeback.value.date });
    }
    await load();
  } catch (e) {
    comebackError.value = extractApiError(e, t('achievements.saveFailed'));
  } finally {
    saving.value = false;
  }
}

const startRow = computed(() => {
  const start = data.value?.start;
  if (!start) {
    return '';
  }
  const date = formatDay(start.date);
  if (start.mode === 'return') {
    return t('achievements.startRowReturn', { date });
  }
  return t(start.mode === 'custom' ? 'achievements.startRowCustom' : 'achievements.startRow', { date });
});
</script>

<template>
  <div class="space-y-3 sm:space-y-4">
    <!-- Skeletons where blocks 1 and 2 will be: nothing jumps when the answer comes -->
    <div
      v-if="loading"
      class="space-y-3 sm:space-y-4 lg:grid lg:grid-cols-12 lg:gap-4 lg:space-y-0"
      aria-busy="true"
      :aria-label="t('common.loading')"
    >
      <div class="h-[19rem] rounded-3xl bg-slate-900 p-5 sm:rounded-lg lg:col-span-5">
        <div class="h-4 w-1/3 rounded bg-slate-800 motion-safe:animate-pulse" />
        <div class="mt-3 h-9 w-1/2 rounded bg-slate-800 motion-safe:animate-pulse" />
        <div class="mt-4 h-2 rounded bg-slate-800 motion-safe:animate-pulse" />
        <div class="mt-3 h-4 w-2/3 rounded bg-slate-800 motion-safe:animate-pulse" />
        <div class="mt-6 h-10 rounded bg-slate-800 motion-safe:animate-pulse" />
      </div>
      <div class="h-[24rem] rounded-3xl bg-slate-900 p-5 sm:rounded-lg lg:col-span-7">
        <div class="h-5 w-2/3 rounded bg-slate-800 motion-safe:animate-pulse" />
        <div class="mt-4 h-8 w-full rounded bg-slate-800 motion-safe:animate-pulse" />
        <div class="mt-4 h-40 w-full rounded bg-slate-800 motion-safe:animate-pulse" />
      </div>
    </div>
    <div
      v-else-if="error"
      class="flex items-center justify-between gap-3 rounded-3xl bg-slate-900 p-5 text-[0.9375rem] text-rose-300 sm:rounded-none sm:bg-transparent sm:p-0 sm:text-sm sm:text-rose-400"
    >
      <span>{{ error }}</span>
      <button
        type="button"
        class="h-11 shrink-0 rounded-2xl bg-slate-800 px-4 text-[0.9375rem] font-medium text-slate-100 sm:h-auto sm:rounded sm:py-1.5 sm:text-sm"
        @click="load"
      >
        {{ t('common.retry') }}
      </button>
    </div>
    <p
      v-else-if="!data || !data.history"
      class="rounded-3xl bg-slate-900 p-6 text-center text-[0.9375rem] text-slate-400 sm:rounded-lg sm:border sm:border-dashed sm:border-slate-700 sm:bg-transparent sm:p-8 sm:text-sm"
    >
      {{ t('achievements.empty') }}
    </p>

    <template v-else>
      <div class="space-y-3 sm:space-y-4 lg:grid lg:grid-cols-12 lg:items-start lg:gap-4 lg:space-y-0">
        <NextMilestone
          class="lg:col-span-5"
          :capital="data.capital"
          :today="data.today"
          @open="open('levels')"
        />
        <ComebackOffer
          v-if="comeback?.offer"
          class="lg:col-span-12 lg:row-start-2"
          :comeback="comeback"
          :busy="saving"
          :error="comebackError"
          @accept="answerComeback(true)"
          @dismiss="answerComeback(false)"
        />
        <AchievementSteps
          class="lg:col-span-7 lg:row-start-1"
          :steps="data.steps"
          :start="data.start.date"
          :capital="data.capital.current"
        />
      </div>

      <div class="space-y-3 sm:space-y-4 lg:grid lg:grid-cols-12 lg:items-start lg:gap-4 lg:space-y-0">
        <AchievementPath
          class="lg:col-span-5"
          :path="data.path"
          :before="data.before"
          @open="open('path')"
        />
        <AchievementHabits
          class="lg:col-span-7"
          :habits="data.habits"
          :breakdown="data.breakdown"
          :today="data.today"
          @open="open"
        />
      </div>

      <button
        type="button"
        class="flex h-11 w-full items-center justify-between px-1 text-[0.8125rem] text-slate-400 hover:text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:w-auto sm:justify-start sm:gap-2"
        @click="openStartSheet"
      >
        <span>{{ startRow }}</span>
        <span
          class="text-slate-500"
          aria-hidden="true"
        >›</span>
      </button>
    </template>

    <BottomSheet
      :open="sheet !== null"
      :title="sheetTitle"
      @close="sheet = null"
    >
      <div
        v-if="sheet && data"
        class="px-5 pb-5 pt-1"
      >
        <template v-if="sheet === 'levels'">
          <div
            class="mb-3 flex gap-2"
            role="tablist"
            :aria-label="t('achievements.levelsTitle')"
          >
            <button
              v-for="key in ['capital', 'shares']"
              :key="key"
              type="button"
              role="tab"
              :aria-selected="ladder === key"
              :class="chipClass(ladder === key)"
              @click="ladder = key"
            >
              {{ t(`achievements.ladder.${key}`) }}
            </button>
          </div>
          <!-- Shares need the history by security: until it is built, say so instead of zeros -->
          <p
            v-if="ladder === 'shares' && !data.breakdown"
            class="py-6 text-center text-[0.9375rem] text-slate-400 sm:text-sm"
          >
            {{ t('achievements.counting') }}
          </p>
          <AchievementLadder
            v-else
            :key="ladder"
            :track="data[ladder]"
            :fold-below="data[ladder].current >= 1000000 ? 100000 : 0"
          />
        </template>
        <AchievementPathList
          v-else-if="sheet === 'path'"
          :path="data.path"
          :before="data.before"
          :mode="data.start.mode"
        />
        <HabitDetail
          v-else
          :kind="sheet"
          :habits="data.habits"
        />
      </div>
    </BottomSheet>

    <BottomSheet
      :open="startSheetOpen"
      :title="t('achievements.startTitle')"
      @close="startSheetOpen = false"
    >
      <fieldset class="space-y-1 px-5 pb-5 pt-2">
        <legend class="mb-2 text-[0.9375rem] text-slate-400 sm:text-sm">
          {{ t('achievements.startHint') }}
        </legend>
        <label class="flex min-h-12 cursor-pointer items-center gap-3 text-[0.9375rem] text-slate-100 sm:text-sm">
          <input
            v-model="startMode"
            type="radio"
            value="first"
            class="size-4 accent-slate-100"
          >
          <span>{{ t('achievements.startFirst', { date: formatDay(data?.start?.first) }) }}</span>
        </label>
        <label
          v-if="comeback"
          class="flex min-h-12 cursor-pointer items-center gap-3 text-[0.9375rem] text-slate-100 sm:text-sm"
        >
          <input
            v-model="startMode"
            type="radio"
            value="return"
            class="size-4 accent-slate-100"
          >
          <span>
            {{ t('achievements.startReturn', { date: formatDay(comeback.date) }) }}
            <span class="block text-[0.8125rem] text-slate-400">{{ t('achievements.startReturnNote', { months: comeback.pause_months, month: formatMonth(comeback.month) }) }}</span>
          </span>
        </label>
        <label class="flex min-h-12 cursor-pointer items-center gap-3 text-[0.9375rem] text-slate-100 sm:text-sm">
          <input
            v-model="startMode"
            type="radio"
            value="custom"
            class="size-4 accent-slate-100"
          >
          <span>{{ t('achievements.startCustom') }}</span>
        </label>
        <input
          v-if="startMode === 'custom'"
          v-model="startInput"
          type="date"
          :aria-label="t('achievements.startCustom')"
          :min="data?.start?.first ?? undefined"
          :max="data?.today || undefined"
          class="h-11 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 text-[0.9375rem] text-slate-100 [color-scheme:dark]"
        >
        <p
          v-if="saveError"
          class="text-[0.8125rem] text-rose-300"
        >
          {{ saveError }}
        </p>
        <button
          type="button"
          class="!mt-3 h-11 w-full rounded-2xl bg-slate-100 text-[0.9375rem] font-medium text-slate-950 disabled:opacity-40"
          :disabled="saving || (startMode === 'custom' && !startInput)"
          @click="applyStart"
        >
          {{ t('achievements.save') }}
        </button>
      </fieldset>
    </BottomSheet>
  </div>
</template>
